import { ArrowDownTrayIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatFileSize } from '../../data/documents';
import {
  getReferencePreview,
  listReferenceItems,
  referenceContentUrl,
  referenceItemContentUrl
} from '../../data/reference';
import type { ReferenceEntryItemSummary, ReferencePreview as Preview } from '../../data/types';

const ITEM_PAGE = 24;

interface Props {
  scope: string;
  entryKey: string;
}

/**
 * Rendered inside a modal rather than as a new tab: the browser would otherwise
 * decide render-vs-download from the MIME type, and a download shows nothing.
 */
export function ReferencePreview({ scope, entryKey }: Props) {
  const { t } = useTranslation();

  const [preview, setPreview] = useState<Preview | null>(null);
  const [items, setItems] = useState<ReferenceEntryItemSummary[]>([]);
  const [itemCount, setItemCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getReferencePreview(scope, entryKey)
      .then((data) => {
        setPreview(data);
        setItems(data.items);
        setItemCount(data.entry.itemCount);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : t('reference.previewFailed'));
      });
  }, [scope, entryKey, t]);

  const loadMore = async () => {
    setBusy(true);
    try {
      const page = await listReferenceItems(scope, entryKey, items.length, ITEM_PAGE);
      setItems((current) => [...current, ...page.items]);
      setItemCount(page.itemCount);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('reference.previewFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <p role="alert" className="border-l-4 border-destructive bg-destructive-soft px-4 py-3 text-sm text-destructive">
        {error}
      </p>
    );
  }

  if (!preview) {
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  }

  const { entry } = preview;

  return (
    <div className="flex flex-col gap-4">
      <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <div className="flex gap-1.5">
          <dt className="text-muted-foreground">{t('reference.columns.kind')}</dt>
          <dd className="text-foreground">{t(`reference.kinds.${entry.kind}`)}</dd>
        </div>
        {entry.fileName ? (
          <div className="flex gap-1.5">
            <dt className="text-muted-foreground">{t('reference.previewFile')}</dt>
            <dd className="text-foreground">{entry.fileName}</dd>
          </div>
        ) : null}
        {entry.fileSize > 0 ? (
          <div className="flex gap-1.5">
            <dt className="text-muted-foreground">{t('reference.previewSize')}</dt>
            <dd className="text-foreground">{formatFileSize(entry.fileSize)}</dd>
          </div>
        ) : null}
        {entry.libraryItemId ? (
          <div className="flex gap-1.5">
            <dt className="text-muted-foreground">{t('reference.previewLibraryItem')}</dt>
            <dd className="font-mono text-foreground">{entry.libraryItemId}</dd>
          </div>
        ) : null}
      </dl>
      <PreviewBody preview={preview} scope={scope} entryKey={entryKey} items={items} />

      {entry.libraryWebUrl ? (
        <div>
          <a
            href={entry.libraryWebUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded border border-input bg-surface px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-muted"
          >
            <ArrowTopRightOnSquareIcon className="size-4" aria-hidden="true" />
            {t('reference.openInSharePoint')}
          </a>
        </div>
      ) : null}

      {entry.kind === 'imageCollection' && items.length < itemCount ? (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            {t('reference.previewItems', { shown: items.length, total: itemCount })}
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={() => void loadMore()}
            className="inline-flex items-center rounded border border-input bg-surface px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-muted disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('reference.previewLoadMore')}
          </button>
        </div>
      ) : null}

      {preview.hasContent ? (
        <div>
          <a
            href={referenceContentUrl(scope, entryKey, true)}
            className="inline-flex items-center gap-2 rounded border border-input bg-surface px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-muted"
          >
            <ArrowDownTrayIcon className="size-4" aria-hidden="true" />
            {t('common.download')}
          </a>
        </div>
      ) : null}
    </div>
  );
}

function PreviewBody({
  preview,
  scope,
  entryKey,
  items
}: Props & { preview: Preview; items: ReferenceEntryItemSummary[] }) {
  const { t } = useTranslation();
  const { entry } = preview;

  if (entry.kind === 'text') {
    return entry.value ? (
      <pre className="max-h-96 overflow-auto rounded border border-border bg-muted p-3 text-sm whitespace-pre-wrap text-foreground">
        {entry.value}
      </pre>
    ) : (
      <p className="text-sm text-muted-foreground">{t('reference.previewEmpty')}</p>
    );
  }

  if (entry.kind === 'imageCollection') {
    return items.length === 0 ? (
      <p className="text-sm text-muted-foreground">{t('reference.previewEmpty')}</p>
    ) : (
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {items.map((item) => (
          <li key={item.id} className="rounded border border-border p-2">
            <img
              src={referenceItemContentUrl(scope, entryKey, item.id)}
              alt={item.fileName}
              loading="lazy"
              className="mx-auto h-24 w-full object-contain"
            />
            <p className="mt-1 truncate text-xs text-muted-foreground" title={item.fileName}>
              {item.fileName}
            </p>
          </li>
        ))}
      </ul>
    );
  }

  if (entry.kind === 'table') {
    return preview.rows.length === 0 ? (
      <p className="text-sm text-muted-foreground">{t('reference.previewNoRows')}</p>
    ) : (
      <div className="overflow-auto rounded border border-border">
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-muted">
            <tr>
              {preview.columns.map((column) => (
                <th key={column} scope="col" className="px-3 py-2 text-left font-semibold text-foreground">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {preview.rows.map((row, index) => (
              // Reference rows carry no id, and the list is read-only, so the index is stable.
              <tr key={index}>
                {row.map((cell, cellIndex) => (
                  <td key={preview.columns[cellIndex]} className="px-3 py-2 text-muted-foreground">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="border-t border-border bg-muted px-3 py-2 text-xs text-muted-foreground">
          {t('reference.previewRows', { shown: preview.rows.length, total: preview.rowCount })}
        </p>
      </div>
    );
  }

  if (entry.fileCategory === 'image') {
    return (
      <div className="flex min-h-48 items-center justify-center rounded border border-border bg-muted p-3">
        <img
          src={referenceContentUrl(scope, entryKey)}
          alt={entry.title || entry.key}
          className="max-h-96 max-w-full object-contain"
        />
      </div>
    );
  }

  if (entry.fileCategory === 'pdf') {
    return (
      <iframe
        src={referenceContentUrl(scope, entryKey)}
        title={entry.title || entry.key}
        className="h-96 w-full rounded border border-border"
      />
    );
  }

  return <p className="text-sm text-muted-foreground">{t('reference.previewUnsupported')}</p>;
}
